import clsx from 'clsx';
import { CheckCircle2, Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { Badge, Card, CardHeader, Checkbox, EmptyState, ErrorState, LoadingBlock, Table, Td, Th, Tr } from '../components/ui';
import {
  AsOfControl,
  CHART,
  ContextBar,
  drillHref,
  FigureValue,
  monthLabel,
  PeriodControls,
  ProfitWord,
  ReportPage,
  tooltipStyle,
  useReport,
  useReportParams,
  type Figure,
} from './FinancialReports';

/* -------------------------------------------------------------------------- */
/* Profit and loss (§6)                                                        */
/* -------------------------------------------------------------------------- */

interface Row {
  figure: Figure;
  months: Array<{ month: string; figure: Figure }>;
}

export function ProfitAndLossPage() {
  const { t, locale, localized } = useI18n();
  const { from, to } = useReportParams();
  const [byMonth, setByMonth] = useState(false);
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/profit-and-loss', { from, to });
  const base: string = data?.context?.baseCurrency ?? 'EGP';

  return (
    <ReportPage
      title={t('nav.frPnl')}
      subtitle={t('fr.pnl.subtitle')}
      controls={
        <div className="flex flex-wrap items-end gap-4">
          <PeriodControls />
          <Checkbox label={t('fr.pnl.showMonths')} checked={byMonth} onChange={(e) => setByMonth(e.target.checked)} />
        </div>
      }
      exportPath="/financial-reports/profit-and-loss/export"
      exportParams={{ from, to }}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <PnlTable data={data} base={base} byMonth={byMonth} from={from} to={to} categoryName={(id) => {
          const c = data.categories.find((x: any) => x.id === id);
          return c ? localized(c) : t('fr.pnl.uncategorized');
        }} locale={locale} />
      )}
    </ReportPage>
  );
}

function PnlTable({
  data,
  base,
  byMonth,
  from,
  to,
  categoryName,
  locale,
}: {
  data: any;
  base: string;
  byMonth: boolean;
  from: string;
  to: string;
  categoryName: (id: string | null) => string;
  locale: 'en' | 'ar';
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const pnl = data.pnl;
  const months: string[] = byMonth ? pnl.months : [];
  const span = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    const last = `${month.slice(0, 8)}${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
    return { from: month < from ? from : month, to: last > to ? to : last };
  };

  const line = (
    label: ReactNode,
    row: Row,
    opts: { level?: 0 | 1 | 2; strong?: boolean; signed?: boolean; drill?: { line: string; categoryId?: string | null } } = {},
  ) => {
    const { level = 1, strong = false, signed = false, drill } = opts;
    return (
      <Tr key={String(label)} className={clsx(strong && 'bg-[var(--surface-sunken)]')}>
        <Td className={clsx(level === 2 && 'ps-8 text-muted', level === 1 && 'ps-5', strong && 'font-semibold')}>
          <span className="inline-flex items-center gap-2">
            {label}
            {signed && <ProfitWord value={row.figure.base} />}
          </span>
        </Td>
        {months.map((month, i) => (
          <Td key={month} align="end" className={clsx(strong && 'font-semibold')}>
            {drill && Number(row.months[i].figure.base) !== 0 ? (
              <button type="button" className="hover:underline" title={t('fr.pnl.drill')} onClick={() => navigate(drillHref(drill.line, { ...span(month), categoryId: drill.categoryId }))}>
                <FigureValue figure={row.months[i].figure} currency={base} signed={signed} />
              </button>
            ) : (
              <FigureValue figure={row.months[i].figure} currency={base} signed={signed} />
            )}
          </Td>
        ))}
        <Td align="end" className={clsx(strong && 'font-semibold')}>
          {drill && Number(row.figure.base) !== 0 ? (
            <button type="button" className="hover:underline" title={t('fr.pnl.drill')} onClick={() => navigate(drillHref(drill.line, { from, to, categoryId: drill.categoryId }))}>
              <FigureValue figure={row.figure} currency={base} signed={signed} />
            </button>
          ) : (
            <FigureValue figure={row.figure} currency={base} signed={signed} />
          )}
        </Td>
      </Tr>
    );
  };
  const section = (label: string) => (
    <Tr key={`section-${label}`}>
      <Td className="pt-5 text-overline uppercase text-[var(--text-tertiary)]" colSpan={months.length + 2}>
        {label}
      </Td>
    </Tr>
  );
  const unsupported = (label: string) => (
    <Tr key={label}>
      <Td className="ps-5 text-muted">{label}</Td>
      {months.map((m) => (
        <Td key={m} />
      ))}
      <Td align="end" className="text-caption text-muted">
        {t('fr.pnl.notRecorded')}
      </Td>
    </Tr>
  );

  const e = pnl.expenses;
  const currencies = [...new Set([...pnl.revenue.net.figure.byCurrency, ...e.total.figure.byCurrency].map((c: any) => c.currency))].sort() as string[];
  const amountIn = (f: Figure, c: string) => f.byCurrency.find((x) => x.currency === c)?.amount ?? '0.00';

  return (
    <div className="space-y-5">
      <ContextBar context={data.context} />
      <Card padded={false}>
        <Table>
          <thead>
            <tr>
              <Th className="min-w-[16rem]">{t('fr.pnl.revenue')} / {t('fr.pnl.operatingExpenses')}</Th>
              {months.map((m) => (
                <Th key={m} align="end">
                  {monthLabel(m, locale, true)}
                </Th>
              ))}
              <Th align="end">{t('fr.total')}</Th>
            </tr>
          </thead>
          <tbody>
            {byMonth && (
              <Tr>
                <Td className="text-caption text-muted">{t('fr.pnl.coverageRow')}</Td>
                {data.coverage.map((c: any) => (
                  <Td key={c.month} align="end">
                    <div className="flex flex-col items-end gap-0.5">
                      <Badge tone={c.profit === 'COMPLETE' ? 'success' : c.profit === 'NOT_RECONCILED' ? 'error' : 'neutral'}>
                        {t(`fr.coverage.profit.${c.profit}` as TranslationKey)}
                      </Badge>
                      {c.partialMonth && <span className="text-caption text-muted">{t('fr.coverage.partialMonth')}</span>}
                    </div>
                  </Td>
                ))}
                <Td />
              </Tr>
            )}
            {section(t('fr.pnl.revenue'))}
            {line(t('fr.pnl.customerPayments'), pnl.revenue.customerPayments, { drill: { line: 'CUSTOMER_PAYMENTS' } })}
            {line(t('fr.pnl.refunds'), pnl.revenue.refunds, { drill: { line: 'REFUNDS' } })}
            {line(t('fr.pnl.netRevenue'), pnl.revenue.net, { strong: true, level: 0 })}
            {section(t('fr.pnl.operatingExpenses'))}
            {line(t('fr.pnl.salariesCurrent'), e.salaries.current)}
            {e.salaries.currentParts.map((p: any) => line(t(`fr.pnl.line.${p.line}` as TranslationKey), p.row, { level: 2, drill: { line: p.line } }))}
            {line(t('fr.pnl.salariesHistorical'), e.salaries.historical, { drill: { line: 'HISTORICAL_SALARIES' } })}
            {line(t('fr.pnl.otherCurrent'), e.other.current, { drill: { line: 'OTHER_EXPENSES' } })}
            {e.other.currentByCategory.map((c: any) => line(categoryName(c.categoryId), c.row, { level: 2, drill: { line: 'OTHER_EXPENSES', categoryId: c.categoryId } }))}
            {line(t('fr.pnl.otherHistorical'), e.other.historical, { drill: { line: 'HISTORICAL_EXPENSES' } })}
            {line(t('fr.pnl.totalExpenses'), e.total, { strong: true, level: 0 })}
            {line(t('fr.pnl.operatingProfit'), pnl.operatingProfit, { strong: true, level: 0, signed: true })}
            {unsupported(t('fr.pnl.nonOperating'))}
            {unsupported(t('fr.pnl.taxes'))}
            {line(t('fr.pnl.netProfit'), pnl.netProfit, { strong: true, level: 0, signed: true })}
          </tbody>
        </Table>
      </Card>

      {currencies.length > 0 && (
        <Card padded={false}>
          <CardHeader title={t('fr.byCurrency')} className="p-5 pb-0" />
          <Table>
            <thead>
              <tr>
                <Th>{t('fr.currency')}</Th>
                <Th align="end">{t('fr.pnl.netRevenue')}</Th>
                <Th align="end">{t('fr.pnl.totalExpenses')}</Th>
                <Th align="end">{t('fr.pnl.netProfit')}</Th>
              </tr>
            </thead>
            <tbody>
              {currencies.map((c) => (
                <Tr key={c}>
                  <Td className="font-mono text-mono-sm">{c}</Td>
                  <Td align="end" className="tabular">{formatMoney(amountIn(pnl.revenue.net.figure, c), c, locale)}</Td>
                  <Td align="end" className="tabular">{formatMoney(amountIn(e.total.figure, c), c, locale)}</Td>
                  <Td align="end" className={clsx('tabular', Number(amountIn(pnl.netProfit.figure, c)) < 0 && 'text-error-fg dark:text-error-dark')}>
                    {formatMoney(amountIn(pnl.netProfit.figure, c), c, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <p className="text-caption text-muted">{t('fr.pnl.noFinancing')}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Balance sheet (§7)                                                          */
/* -------------------------------------------------------------------------- */

export function BalanceSheetPage() {
  const { t, locale } = useI18n();
  const { asOf } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/balance-sheet', { asOf });

  return (
    <ReportPage title={t('nav.frBalanceSheet')} subtitle={t('fr.bs.subtitle')} controls={<AsOfControl />} exportPath="/financial-reports/balance-sheet/export" exportParams={{ asOf }}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          {data.currencies.length === 0 ? (
            <Card>
              <EmptyState icon={<Lock className="h-6 w-6" />} title={t(`fr.status.${data.context.status}` as TranslationKey)} hint={t(`fr.statusHint.${data.context.status}` as TranslationKey)} />
            </Card>
          ) : (
            <div className="grid gap-5 xl:grid-cols-2">
              {data.currencies.map((c: any) => (
                <BalanceSheetCard key={c.currency} sheet={c} locale={locale} />
              ))}
            </div>
          )}
          {data.historicalReachingCutover?.length > 0 && (
            <Card>
              <CardHeader title={t('fr.bs.historical')} subtitle={t('fr.bs.historicalHint')} />
              <ul className="space-y-1 text-body-sm">
                {data.historicalReachingCutover.map((h: any) => (
                  <li key={h.id}>
                    <Link to={`/historical/${h.id}`} className="text-primary-600 hover:underline dark:text-primary-400">
                      {monthLabel(h.month, locale)} · {formatMoney(h.amount, h.currency, locale)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <p className="text-caption text-muted">{t('fr.bs.noReceivables')}</p>
        </div>
      )}
    </ReportPage>
  );
}

function BalanceSheetCard({ sheet, locale }: { sheet: any; locale: 'en' | 'ar' }) {
  const { t } = useI18n();
  const c = sheet.currency;
  const row = (label: string, value: string, opts: { strong?: boolean; negative?: boolean } = {}) => (
    <Tr key={label} className={clsx(opts.strong && 'bg-[var(--surface-sunken)]')}>
      <Td className={clsx(opts.strong ? 'font-semibold' : 'ps-5')}>{label}</Td>
      <Td align="end" className={clsx('tabular', opts.strong && 'font-semibold', Number(value) < 0 && 'text-error-fg dark:text-error-dark')}>
        {opts.negative && Number(value) !== 0 ? `−${formatMoney(value, c, locale)}` : formatMoney(value, c, locale)}
      </Td>
    </Tr>
  );
  const heading = (label: string) => (
    <Tr key={`h-${label}`}>
      <Td className="pt-4 text-overline uppercase text-[var(--text-tertiary)]" colSpan={2}>
        {label}
      </Td>
    </Tr>
  );
  return (
    <Card padded={false}>
      <CardHeader
        title={<span className="font-mono">{c}</span>}
        className="p-5 pb-0"
        action={
          sheet.balanced ? (
            <Badge tone="success" dot>
              {t('fr.bs.balanced')}
            </Badge>
          ) : (
            <Badge tone="error" dot>
              {t('fr.bs.difference', { amount: formatMoney(sheet.difference, c, locale) })}
            </Badge>
          )
        }
      />
      <Table>
        <tbody>
          {heading(t('fr.bs.assets'))}
          {row(t('fr.bs.cash'), sheet.assets.cash)}
          {row(t('fr.bs.advances'), sheet.assets.advancesReceivable)}
          {row(t('fr.bs.prepaid'), sheet.assets.prepaidExpenses)}
          {row(t('fr.bs.totalAssets'), sheet.assets.total, { strong: true })}
          {heading(t('fr.bs.liabilities'))}
          {row(t('fr.bs.loans'), sheet.liabilities.partnerLoans)}
          {row(t('fr.bs.unpaid'), sheet.liabilities.unpaidExpenses)}
          {row(t('fr.bs.totalLiabilities'), sheet.liabilities.total, { strong: true })}
          {heading(t('fr.bs.equity'))}
          {row(t('fr.bs.capital'), sheet.equity.partnerCapital)}
          {row(t('fr.bs.openingEquity'), sheet.equity.openingEquity)}
          {row(t('fr.bs.netIncome'), sheet.equity.netIncome)}
          {row(t('fr.bs.distributions'), sheet.equity.distributions, { negative: true })}
          {row(t('fr.bs.adjustments'), sheet.equity.adjustmentsOffset)}
          {row(t('fr.bs.totalEquity'), sheet.equity.total, { strong: true })}
        </tbody>
      </Table>
      <div className="space-y-2 p-5 pt-3">
        <p className={clsx('flex items-center gap-2 text-body-sm', sheet.balanced ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>
          {sheet.balanced && <CheckCircle2 className="h-4 w-4" aria-hidden />}
          {t('fr.bs.check')}: {formatMoney(sheet.assets.total, c, locale)} = {formatMoney(sheet.liabilities.total, c, locale)} + {formatMoney(sheet.equity.total, c, locale)}
        </p>
        <details className="text-caption text-muted">
          <summary className="cursor-pointer">{t('fr.bs.derivation')}</summary>
          <p className="mt-1">
            {t('fr.bs.derivationText', {
              cash: formatMoney(sheet.opening.cash, c, locale),
              advances: formatMoney(sheet.opening.advancesReceivable, c, locale),
              prepaid: formatMoney(sheet.opening.prepaidExpenses, c, locale),
              loans: formatMoney(sheet.opening.partnerLoans, c, locale),
              unpaid: formatMoney(sheet.opening.unpaidExpenses, c, locale),
              capital: formatMoney(sheet.opening.partnerCapital, c, locale),
            })}
          </p>
        </details>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Cash flow (§8)                                                              */
/* -------------------------------------------------------------------------- */

export function CashFlowPage() {
  const { t, locale } = useI18n();
  const { from, to } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/cash-flow', { from, to });

  return (
    <ReportPage title={t('nav.frCashFlow')} subtitle={t('fr.cf.subtitle')} controls={<PeriodControls />} exportPath="/financial-reports/cash-flow/export" exportParams={{ from, to }}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          {data.statements.length === 0 ? (
            <Card>
              <EmptyState icon={<Lock className="h-6 w-6" />} title={t(`fr.status.${data.context.status}` as TranslationKey)} hint={t(`fr.statusHint.${data.context.status}` as TranslationKey)} />
            </Card>
          ) : (
            <>
              <div className="grid gap-5 xl:grid-cols-2">
                {data.statements.map((s: any) => (
                  <CashFlowCard key={s.currency} statement={s} range={data.range} locale={locale} />
                ))}
              </div>
              <AccountsCard accounts={data.accounts} locale={locale} />
            </>
          )}
        </div>
      )}
    </ReportPage>
  );
}

function CashFlowCard({ statement: s, range, locale }: { statement: any; range: { from: string; to: string }; locale: 'en' | 'ar' }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const c = s.currency;
  const money = (v: string) => formatMoney(v, c, locale);
  const section = (title: string, sec: { lines: any[]; net: string }) => (
    <>
      <Tr>
        <Td className="pt-4 text-overline uppercase text-[var(--text-tertiary)]" colSpan={2}>
          {title}
        </Td>
      </Tr>
      {sec.lines.map((l) => {
        const out = ['REFUNDS_PAID', 'SALARIES_PAID', 'POINTS_CASH_PAID', 'OTHER_EXPENSES_PAID', 'PARTNER_LOANS_REPAID', 'DISTRIBUTIONS_PAID', 'ADVANCES_PAID'].includes(l.line);
        const open = Number(l.amount) !== 0;
        return (
          <Tr key={l.line} onClick={open ? () => navigate(drillHref(l.line, { ...range, currency: c })) : undefined}>
            <Td className="ps-5">
              {t(`fr.cf.line.${l.line}` as TranslationKey)}
              {l.count > 0 && <span className="ms-2 text-caption text-muted tabular">· {l.count}</span>}
            </Td>
            <Td align="end" className={clsx('tabular', !open && 'text-muted')}>
              {open && out ? `−${money(l.amount)}` : money(l.amount)}
            </Td>
          </Tr>
        );
      })}
      <Tr>
        <Td className="ps-5 font-medium">{t('fr.cf.net')}</Td>
        <Td align="end" className={clsx('tabular font-medium', Number(sec.net) < 0 && 'text-error-fg dark:text-error-dark')}>
          {money(sec.net)}
        </Td>
      </Tr>
    </>
  );
  const chart = s.byMonth.map((m: any) => ({ label: monthLabel(m.month, locale, true), inflow: Number(m.inflow), outflow: Number(m.outflow) }));
  return (
    <Card padded={false}>
      <CardHeader
        title={<span className="font-mono">{c}</span>}
        subtitle={`${formatDate(range.from, locale)} – ${formatDate(range.to, locale)}`}
        className="p-5 pb-0"
        action={
          s.reconciled ? (
            <Badge tone="success" dot>
              {t('fr.cf.reconciled')}
            </Badge>
          ) : (
            <Badge tone="error" dot>
              {t('fr.cf.difference', { amount: money(s.difference) })}
            </Badge>
          )
        }
      />
      <Table>
        <tbody>
          <Tr className="bg-[var(--surface-sunken)]">
            <Td className="font-semibold">{t('fr.cf.beginning')}</Td>
            <Td align="end" className="tabular font-semibold">{money(s.beginning)}</Td>
          </Tr>
          {section(t('fr.cf.operating'), s.sections.OPERATING)}
          <Tr>
            <Td className="pt-4 text-overline uppercase text-[var(--text-tertiary)]">{t('fr.cf.investing')}</Td>
            <Td align="end" className="pt-4 text-caption text-muted">{t('fr.cf.investingNone')}</Td>
          </Tr>
          {section(t('fr.cf.financing'), s.sections.FINANCING)}
          <Tr className="bg-[var(--surface-sunken)]">
            <Td className="font-semibold">{t('fr.cf.netChange')}</Td>
            <Td align="end" className={clsx('tabular font-semibold', Number(s.netChange) < 0 && 'text-error-fg dark:text-error-dark')}>{money(s.netChange)}</Td>
          </Tr>
          <Tr className="bg-[var(--surface-sunken)]">
            <Td className="font-semibold">{t('fr.cf.ending')}</Td>
            <Td align="end" className="tabular font-semibold">{money(s.ending)}</Td>
          </Tr>
          <Tr>
            <Td className="text-muted">{t('fr.cf.books')}</Td>
            <Td align="end" className="tabular text-muted">{money(s.endingPerBooks)}</Td>
          </Tr>
        </tbody>
      </Table>
      <div className="space-y-3 p-5 pt-3">
        {s.internalTransfers.count > 0 && <p className="text-caption text-muted">{t('fr.cf.transfers', { amount: money(s.internalTransfers.amount) })}</p>}
        {chart.length > 1 && (
          <div className="h-48" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={11} />
                <YAxis stroke="var(--text-tertiary)" fontSize={11} width={64} tickFormatter={(v) => Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { notation: 'compact' }).format(v)} />
                <Tooltip formatter={(v: any) => formatMoney(Number(v), c, locale)} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span className="text-[var(--text-secondary)]">{v}</span>} />
                <Bar name={t('fr.cf.in')} dataKey="inflow" fill={CHART.profit} radius={[3, 3, 0, 0]} />
                <Bar name={t('fr.cf.out')} dataKey="outflow" fill={CHART.expenses} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

function AccountsCard({ accounts, locale }: { accounts: any[]; locale: 'en' | 'ar' }) {
  const { t, localized } = useI18n();
  if (accounts.length === 0) return null;
  return (
    <Card padded={false}>
      <CardHeader title={t('fr.cf.accounts')} subtitle={t('fr.cf.unassignedHint')} className="p-5 pb-0" />
      <Table>
        <thead>
          <tr>
            <Th>{t('fr.cf.account')}</Th>
            <Th>{t('fr.currency')}</Th>
            <Th align="end">{t('fr.cf.in')}</Th>
            <Th align="end">{t('fr.cf.out')}</Th>
            <Th align="end">{t('fr.cf.net')}</Th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <Tr key={`${a.moneyAccountId ?? '-'}-${a.currency}`}>
              <Td>{a.account ? localized(a.account) : <span className="text-muted">{t('fr.cf.unassigned')}</span>}</Td>
              <Td className="font-mono text-mono-sm">{a.currency}</Td>
              <Td align="end" className="tabular">{formatMoney(a.inflow, a.currency, locale)}</Td>
              <Td align="end" className="tabular">{formatMoney(a.outflow, a.currency, locale)}</Td>
              <Td align="end" className={clsx('tabular', Number(a.net) < 0 && 'text-error-fg dark:text-error-dark')}>{formatMoney(a.net, a.currency, locale)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
